import React, { useState } from 'react';
import { Button, Col, Container, Image, Row, Stack, Form } from 'react-bootstrap';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { useNavigate, useParams } from 'react-router-dom';
import {
	useGetCheckSubscriptionMutation,
	useAddToTalentMutation,
	useGetSeekerAssessmentQuery,
} from 'services';
import SaveTalentBtn from 'components/Common/ActionButton/SaveTalent';
import { useQueryClient } from '@tanstack/react-query';
import { S3_URL } from 'constants';
import Loader from 'components/Common/Loader';
import useCommonStore, { setModalConfig } from 'store/common';
import useAuthStore from 'store/auth';
import ChatBtn from 'components/Common/ActionButton/ChatBtn';
import useTranslate from 'hooks/useTranslate';
import './styles.scss';

const ApplicantAssessment: React.FC = () => {
	const {
		user: { companyName = '' },
	} = useAuthStore((state: any) => state?.userInfo ?? {});
	const { jobId, userId } = useParams<any>();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { translate } = useTranslate();
	const { data, isPending } = useGetSeekerAssessmentQuery({
		jobId: Number(jobId),
		applicantId: Number(userId),
	});
	const [isFavourite, setIsFavourite] = useState<boolean>(data?.isSaved);
	const { mutateAsync: addToFavMutation } = useAddToTalentMutation();
	const { mutateAsync: checkSubscriptionMutation } = useGetCheckSubscriptionMutation();
	const { hideCommonModal }: any = useCommonStore((state) => state);

	const onScheduleMeetingClick = () => {
		checkSubscriptionMutation('schedule-meeting').then((res) => {
			if (res.success) {
				setModalConfig({
					visible: true,
					id: userId,
					type: 'meetingScheduleModalCompany',
					data: {
						userName: data?.applicantName,
						profilePicture: data?.applicantProfilePicture,
						jobId,
						seekerDesc: `${data?.applicantDesignation} at ${data?.applicantCompanyName}`,
						companyDesc: `${data?.jobTitle} with ${companyName}`,
					},
					props: {
						dialogClassName: 'schedule-meeting-dialog',
					},
				});
			} else {
				setModalConfig({
					visible: true,
					type: 'subscriptionAlert',
					onClose: () => {
						hideCommonModal();
					},
					onClick: () => {
						navigate('/company/subscriptions');
						hideCommonModal();
					},
				});
			}
		});
	};

	const addToFavorite = () => {
		if (userId) {
			setIsFavourite((prev) => !prev);
			addToFavMutation({ userId: userId })
				.then((_res: any) => {
					queryClient.invalidateQueries({ queryKey: ['get-saved-talent'] });
				})
				.catch(() => {
					setIsFavourite((prev) => !prev);
				});
		}
	};

	const goToProfile = (id: any) => {
		navigate(`/seeker/profile/${id}`);
	};

	return (
		<section className="applicant-assessment py-50">
			<Container>
				<Stack className="applicant-assessment-inner">
					<div className="white-box bg-white">
						{data ? (
							<div className="p-1">
								<h4 className="fw-bold mb-0">
									{translate('Questionnaire.assessmentQuestionnaireResponseFor')}{' '}
									{data?.jobTitle &&
										[data?.jobTitle, data?.jobLocation].filter(Boolean).join(' at ')}
								</h4>
								<Stack className="applicant-detail gap-3 gap-md-4">
									<div className="d-flex align-items-center gap-3 position-relative">
										<div className="d-flex flex-column flex-md-row align-items-md-center gap-3 flex-1">
											<div
												className="applicant-img"
												style={{ cursor: 'pointer' }}
												onClick={() => goToProfile(userId)}
											>
												<Image
													src={
														data?.applicantProfilePicture
															? `${S3_URL}${data.applicantProfilePicture}`
															: IMAGE_PATHS.UserDefault
													}
													alt="user-img"
													className="w-100 h-100 object-fit-cover rounded-circle"
													loading="lazy"
												/>
											</div>
											<Stack
												style={{ cursor: 'pointer' }}
												className="applicant-info justify-content-center gap-1"
											>
												{data?.applicantName && (
													<h5 onClick={() => goToProfile(userId)} className="fw-bold mb-0">
														{data.applicantName}
													</h5>
												)}
												{data?.applicantDesignation && (
													<p className="mb-0">{data.applicantDesignation}</p>
												)}
												{data?.applicantCompanyName && (
													<p className="mb-0">{data.applicantCompanyName}</p>
												)}
											</Stack>
										</div>
										<div className="applicant-actions d-flex gap-2 gap-md-3">
											<ChatBtn
												className="edit-btn p-0 rounded-circle"
												receiverId={Number(userId)}
												mode="icon"
											/>

											<SaveTalentBtn
												isFavourite={isFavourite}
												handleClick={(e: any) => {
													e.stopPropagation();
													addToFavorite();
												}}
												className="filled-btn fav-btn rounded-pill lh-1"
											/>
										</div>
									</div>
									{data?.appliedStatus !== 'rejected' && (
										<div className="applicant-detail-actions d-flex flex-column flex-md-row gap-2 gap-md-3">
											<Button
												onClick={(e: any) => {
													e.stopPropagation();
													setModalConfig({
														visible: true,
														id: data?.jobApplicationId,
														type: 'rejectModal',
													});
												}}
												className="gray-transparent-btn rounded-pill lh-1"
											>
												{translate('Common.reject')}
											</Button>
											<Button
												onClick={() => onScheduleMeetingClick()}
												className="filled-btn rounded-pill lh-1"
											>
												{translate('Interview.scheduleInterview')}
											</Button>
										</div>
									)}
								</Stack>
							</div>
						) : isPending ? (
							<Loader />
						) : (
							translate('Common.noDataFound')
						)}
					</div>
					{data?.questionList?.map((item: any, index: number) => (
						<div className="white-box bg-white" key={index}>
							<Stack className="gap-2 gap-md-3 px-0 px-md-3">
								<h5 className="applicant-assessment-question fw-bold mb-0 d-flex gap-2">
									<span>{`Q${index + 1}.`}</span>
									{item?.question}
								</h5>
								<Row className="applicant-assessment-options">
									{item?.options?.map((ans: string, index: number) => {
										const isSelected = item?.seekerAnswer === ans;
										return (
											<Col md={6} key={index}>
												{/* <label className="d-flex align-items-center">
													<input
														type="radio"
														name={`question-${item.id}`}
														value={ans}
														checked={isSelected}
														readOnly
														className="me-2"
													/>
													<p className="applicant-assessment-answer mb-0">{ans}</p>
												</label> */}

												<div
													key="radio"
													className="form-check-radio d-flex flex-wrap gap-2 gap-md-4"
												>
													<Form.Check
														inline
														label={ans}
														name={`question-${item.id}`}
														type="radio"
														value={ans}
														checked={isSelected}
														readOnly
													/>
												</div>
											</Col>
										);
									})}
								</Row>
							</Stack>
						</div>
					))}
				</Stack>
			</Container>
		</section>
	);
};

export default ApplicantAssessment;
